import { useEffect } from "react";
import type { LastBotCore } from "../core/LastBotCore";
import type { LastBotState } from "../core/state";
import storageService, { type PageStorageScope } from "../core/services/storageService";

/**
 * Structural slice of LastBotCore the persistence hook needs — kept narrow so
 * tests can fake it with an Emitter subclass.
 */
export interface ThreadPersistenceCore {
  on: LastBotCore["on"];
  off: LastBotCore["off"];
  getState: () => Pick<LastBotState, "thread">;
}

/**
 * Mirrors the active thread into the scoped page storage so a host-page reload
 * restores the conversation (LAS-1492). Mounted once in App — NOT in ChatWidget,
 * which only renders while the panel is open — so embedder-driven
 * core.startNewThread()/closeThread() with the panel closed persist too. This
 * is the single THREAD-DRIVEN writer of the stored page — it covers ALL
 * thread-open paths (composer send, conversation starters, history selection,
 * embedder startNewThread()), in both the multi-page UI and hasCustomMainPage
 * mode. (Page navigation writes the same key on its own path: navigate() in
 * ChatWidget and start-message seeding in App.)
 *
 * - thread:opened → persist { type: "conversation", params: { messageThreadId } }.
 * - thread:closed with state.thread === null (client-side closeThread(), which
 *   nulls state.thread BEFORE emitting — contract locked in
 *   LastBotCore.thread.test.ts) → reset the stored page to landing so a reload
 *   starts fresh.
 * - thread:closed with state.thread still set (server closed the task —
 *   closedAt set by handleTaskUpdated) → keep the stored reference so a reload
 *   still shows the closed conversation, matching chat-history behavior.
 */
export const useThreadPagePersistence = (
  core: ThreadPersistenceCore,
  scope: PageStorageScope,
): void => {
  useEffect(() => {
    const onThreadOpened = ({ threadId }: { threadId: number }): void => {
      storageService.setPage(scope, {
        type: "conversation",
        params: { messageThreadId: threadId.toString() },
      });
    };
    const onThreadClosed = (): void => {
      if (core.getState().thread === null) {
        storageService.setPage(scope, { type: "landing" });
      }
    };
    const offOpened = core.on("thread:opened", onThreadOpened);
    const offClosed = core.on("thread:closed", onThreadClosed);
    return () => {
      offOpened();
      offClosed();
    };
  }, [core, scope]);
};
